import React from "react";
import { filter, map, uniq } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { getCategoryFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import Panel from "@ui-kit/popover/panel";
import MultipleList from "@radarly/components/filterbar/panels/multiple-list";
import { useFilterbarChoices } from "@radarly/core/filters/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { transformClauseFromField } from "@radarly/models/clause/transformer";
import { FullFacet } from "@radarly/models/facets";
import { Choice, Status } from "@radarly/models/filters";
import {
  getNextClauseFromStatusAndValue,
  getNextClauseFromStatusAndValues,
} from "./clause";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const getChoicesByCategory = (choices: Choice[], category: string) =>
  filter(choices, (choice) => choice.category === category).map((c) => c.id);

const RadarlyFilterbarPanelsMultipleList = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const { choices, orderedChoices } = useFilterbarChoices(clause, facet);
  const categories = map(uniq(map(choices, "category")), (category) => {
    return {
      id: category,
      label: getCategoryFacetName(locale, facet.id as Facet, category),
    };
  });

  const onSelect = (choice: Choice, status: Status) => {
    const isFullCategory = categories.some(({ id }) => id === choice.id);
    if (isFullCategory) {
      return setClause(
        getNextClauseFromStatusAndValues(
          clause,
          facet,
          getChoicesByCategory(choices, choice.category),
          status
        )
      );
    }
    return setClause(
      getNextClauseFromStatusAndValue(clause, facet, choice.id, status)
    );
  };

  const onGlobalSelect = (status: Status, category?: string) => {
    setClause(
      getNextClauseFromStatusAndValues(
        clause,
        facet,
        getChoicesByCategory(choices, category),
        status
      )
    );
  };

  const onClear = () => {
    setClause(
      refineClauseWithInnerClause(
        clause,
        transformClauseFromField(
          {
            [facet.id]: [],
          },
          facet.custom ? [facet.id] : []
        )
      )
    );
  };

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size={choices.length < 5 ? "s" : "m"}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <MultipleList
        choices={choices}
        orderedChoices={orderedChoices}
        allowExclusion
        onSelect={onSelect}
        onGlobalSelect={onGlobalSelect}
        globalSelectors={["include", "exclude", "none"]}
        locale={locale}
        categories={categories}
        placeholder={tr(
          locale,
          "FILTER_PANEL::SEARCH_PLACEHOLDER::" + facet.id
        )}
      />
    </Panel>
  );
};

export default RadarlyFilterbarPanelsMultipleList;
